import React, { useState, useEffect } from "react";
import { Button, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthPageLayout from "../../components/layout/AuthPageLayout";
import VerificationCodeInput from "../../components/VerificationCodeInput";
import { FormErrorContainer } from "../../components/FormErrorContainer";
import { ROUTES, API_ENDPOINTS } from "../../constants";
import api from "../../api/axios";
import { DEMO_PHONE_KEY } from "./DemoPhonePage";
import {
  VerificationContainer,
  VerificationTitle,
  VerificationSubtitle,
  VerificationForm,
  VerificationActions,
} from "../Auth/VerificationPage/VerificationPage.styled";

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? "";

const DemoVerifyPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [countdown, setCountdown] = useState(60);
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  const phoneNumber = sessionStorage.getItem(DEMO_PHONE_KEY) ?? "";

  // Countdown timer for Resend button
  useEffect(() => {
    if (countdown <= 0) {
      setIsResendDisabled(false);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleVerify = async () => {
    if (code.length !== 6 || isSubmitting) return;

    setApiErrors([]);
    setIsSubmitting(true);

    try {
      // TODO: confirm exact payload/endpoint with backend for demo OTP verification
      await api.post(API_ENDPOINTS.VERIFY_2FA, { phoneNumber, code });
      navigate(ROUTES.DEMO_SUCCESS);
    } catch {
      setApiErrors([
        t("verification.error", "Invalid code. Please try again."),
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!phoneNumber) {
      setApiErrors([
        t(
          "demo.phone.missingPhone",
          "Phone number not found. Please go back and re-enter your number.",
        ),
      ]);
      return;
    }

    setApiErrors([]);

    try {
      await api.post(
        `${API_BASE.replace(/\/$/, "")}/testing/sms`,
        null,
        { params: { phoneNumber } },
      );
      setCountdown(60);
      setIsResendDisabled(true);
    } catch {
      setApiErrors([
        t("demo.phone.sendError", "Failed to resend code. Please try again."),
      ]);
    }
  };

  return (
    <AuthPageLayout contentWidth="694px">
      <VerificationContainer>
        <VerificationTitle>{t("verification.title")}</VerificationTitle>

        <VerificationSubtitle>
          {t("verification.subtitle")}
        </VerificationSubtitle>

        <VerificationForm>
          <VerificationCodeInput
            length={6}
            onComplete={() => {}}
            onChange={setCode}
            error={false}
          />

          <FormErrorContainer errors={apiErrors} />

          <Box sx={{ textAlign: "center", fontSize: "14px" }}>
            {t("verification.didntGetCode")}{" "}
            {isResendDisabled ? (
              <Box component="span" sx={{ color: "#666" }}>
                {t("verification.resendIn", { seconds: countdown })}
              </Box>
            ) : (
              <Button
                variant="text"
                onClick={handleResend}
                disabled={isSubmitting}
                sx={{
                  minWidth: "auto",
                  p: 0,
                  textTransform: "none",
                  fontSize: "14px",
                  color: "primary.main",
                }}
              >
                {t("verification.resendCode")}
              </Button>
            )}
          </Box>

          <VerificationActions>
            <Button
              variant="outlined"
              onClick={handleResend}
              disabled={isResendDisabled || isSubmitting}
            >
              {t("verification.resendCode")}
            </Button>

            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={code.length !== 6 || isSubmitting}
            >
              {t("verification.verify")}
            </Button>
          </VerificationActions>
        </VerificationForm>
      </VerificationContainer>
    </AuthPageLayout>
  );
};

export default DemoVerifyPage;
